import React from "react";
import { Building2, History, Award, Users2, TrendingUp } from "lucide-react";
import Organogram from "../Components/Organogram";

const AboutUsOption1 = () => {
    return (
        <div
            className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 "
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
        >
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-[#005A8D] via-[#0077B6] to-[#005A8D] py-10 mt-4 ">
                <div className="max-w-[95%] mx-auto px-3 md:px-4 text-center -mb-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
                        About Us
                    </h1>
                    <div className="w-20 h-1 bg-white mx-auto mb-3"></div>
                    <p className="text-lg text-white/90 max-w-2xl mx-auto">
                        Pioneering Cooperative Excellence in Tamil Nadu
                    </p>
                </div>
            </div>

            <div className="max-w-[95%] mx-auto px-3 md:px-4 py-6">

                {/* History Section */}
                <div className="p-5 md:p-6 shadow-xl mb-5" style={{ backgroundColor: '#f2f2f2' }}>
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-3 bg-gradient-to-br from-[#005A8D] to-[#0077B6] rounded-lg">
                            <History className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">Our History</h2>
                    </div>

                    <div className="space-y-4 text-gray-700 text-xl leading-relaxed text-justify">
                        <p>
                            The cooperative movement in Tamil Nadu traces its roots back to the early 20th century, when the first cooperative societies were established to provide financial assistance to farmers and rural communities. The Cooperative Credit Societies Act of 1904 laid the foundation for organized cooperative activities in the region, primarily focusing on agricultural credit and rural development.
                        </p>

                        <p>
                            The Tamil Nadu Cooperative Societies Act was subsequently enacted to provide a comprehensive legal framework for the formation, functioning, and regulation of cooperatives across the state. This legislation empowered the Registrar of Cooperative Societies to oversee and guide cooperative institutions, ensuring they adhered to democratic principles and served their members effectively.
                        </p>

                        <p>
                            Over the decades, the cooperative sector has evolved significantly, expanding its reach from traditional credit societies to diverse areas including consumer cooperatives, housing cooperatives, producer cooperatives, and multipurpose societies. The establishment of cooperative banks, Primary Agricultural Credit Societies (PACS), and apex institutions strengthened the financial infrastructure for rural and urban communities.
                        </p>

                        <p>
                            Tamil Nadu's cooperative movement played a pivotal role during the Green Revolution, providing timely credit, quality inputs, and technical guidance to farmers. The sector adapted to changing needs by introducing innovative schemes such as the Public Distribution System (PDS) through fair price shops, Market Intervention Schemes, and welfare programs like Amma Marundhagam pharmacies and Farm Fresh outlets.
                        </p>

                        <p>
                            Today, Tamil Nadu stands as a model state in cooperative governance, with thousands of registered societies serving millions of members across urban and rural areas. The department continues to innovate and adapt to changing socio-economic conditions while maintaining its commitment to empowering communities through democratic participation, financial inclusion, and sustainable development.
                        </p>
                    </div>
                </div>

                {/* Key Achievements Grid */}
                <div className="grid md:grid-cols-3 gap-4 mb-5">

                    <div className="bg-[#facdd2] text-black p-5 shadow-lg text-center 
                    hover:bg-[#f4b8c2] transition-colors">
                        <Award className="w-8 h-8 mx-auto mb-2" />
                        <h3 className="font-bold text-lg mb-1">1000+</h3>
                        <p className="text-sm text-black/70">Registered Societies</p>
                    </div>

                    <div className="bg-[#c8e5c9] text-black p-5 shadow-lg text-center 
                    hover:bg-[#b6d7b8] transition-colors">
                        <Users2 className="w-8 h-8 mx-auto mb-2" />
                        <h3 className="font-bold text-lg mb-1">8 Million</h3>
                        <p className="text-sm text-black/70">Active Members</p>
                    </div>

                    <div className="bg-[#ffecb2] text-black p-5 shadow-lg text-center 
                    hover:bg-[#f8e29c] transition-colors">
                        <TrendingUp className="w-8 h-8 mx-auto mb-2" />
                        <h3 className="font-bold text-lg mb-1">100+ Years</h3>
                        <p className="text-sm text-black/70">Of Excellence</p>
                    </div>

                </div>
                {/* Administrative Set Up with Image */}
                <div className="grid md:grid-cols-2 gap-5 mb-5">
                    {/* Administrative Structure - Left Column */}
                    <div className="bg-white shadow-xl border-t-4 border-[#005A8D]">
                        <div className="bg-gradient-to-r from-[#005A8D] via-[#0077B6] to-[#005A8D] py-4 px-5">
                            <h2 className="text-xl font-bold text-white text-center flex items-center justify-center gap-2">
                                <Building2 className="w-6 h-6" />
                                Administrative Structure
                            </h2>
                        </div>

                        <div className="p-5 bg-gradient-to-br from-slate-50 to-blue-50 flex justify-center">
                            <div className="flex flex-col space-y-2 w-1/2">
                                {[
                                    { title: "Secretary to Government", subtitle: "Cooperation, Food & Consumer Protection" },
                                    { title: "Registrar of Cooperative Societies", subtitle: "" },
                                    { title: "Joint Registrars", subtitle: "" },
                                    { title: "Deputy Registrars", subtitle: "" },
                                    { title: "Cooperative Sub-Registrars", subtitle: "" },
                                    { title: "Senior Inspectors", subtitle: "" },
                                    { title: "Junior Inspectors", subtitle: "" },
                                ].map((item, index, arr) => (
                                    <React.Fragment key={index}>
                                        <div className="w-full">
                                            <div className="bg-white border-2 border-[#005A8D] shadow px-4 py-2.5 hover:bg-[#005A8D] hover:text-white transition-all duration-300 group">
                                                <p className="text-center text-gray-800 font-semibold text-sm group-hover:text-white">
                                                    {item.title}
                                                </p>
                                                {item.subtitle && (
                                                    <p className="text-center text-xs text-gray-600 mt-0.5 group-hover:text-white/90">
                                                        {item.subtitle}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {index !== arr.length - 1 && (
                                            <div className="text-[#005A8D] text-lg text-center">↓</div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Image Section - Right Column */}
                    <div className="bg-white shadow-xl border-t-4 border-[#005A8D] flex items-center justify-center p-5">
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
                            <img
                                src="/Images/caro3.png"
                                alt="Cooperative Movement"
                                className="w-full h-full object-cover rounded-lg shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Organogram */}
            <Organogram />
        </div>
    );
};

export default AboutUsOption1;